/**
 * spec-008 [T068] — falar por cima CALA a IA, no transporte em tempo real.
 *
 * ## Por que isto existe, se o transporte dizia que não precisava
 *
 * O cabeçalho de `transporte-tempo-real.js` declara que este transporte **não**
 * reimplementa barge-in, porque *"falar por cima já chega ao provedor pelo
 * microfone, que segue aberto"*. A aposta era que o provedor cortaria a própria
 * fala. **Ela é falsa**, e agora está medido nos dois sentidos:
 *
 * 1. **Em uso real** (owner, 17/09): interrompeu no meio de uma resposta para
 *    pedir outra coisa e a IA **não parou — ignorou**. *"Isso não é uma
 *    conversa real."*
 * 2. **Na abertura da sessão** (sonda contra o provedor, mesmo dia): não existe
 *    onde ligar isso. `session.turn_detection`, `session.audio.turn_detection`,
 *    `session.audio.input`, `session.vad`, `session.interruptions` — todos
 *    respondem `Unknown parameter`. O objeto `session.audio` inteiro só aceita
 *    `output` e `format` (e `format` é *"only supported for WebSocket
 *    sessions"*). O controle da sonda são dois campos sabidamente inexistentes,
 *    que responderam a mesma coisa — sem esse par, "não deu erro" não provaria
 *    nada (a validação do SDP roda ANTES e mascara tudo: com um offer inválido
 *    os quatro candidatos e os dois controles davam a MESMA mensagem).
 *
 * Como o protocolo cliente→servidor também não tem evento de cancelar fala
 * (N4, medido na [T036]), **o único lugar onde o barge-in pode acontecer é
 * aqui**. A máquina que executa já existia e estava completa
 * (`transporte-tempo-real-fala.js`: silencia a trilha, descarta os deltas,
 * devolve o áudio depois do silêncio) — faltava **o gatilho**.
 *
 * ## O que este módulo NÃO faz
 *
 * - **Não escreve em `motorDeVoz.estadoDaFala`.** Aquele campo tem `$watch` no
 *   `transporte-por-turnos.js`, e escrever nele armaria o barge-in do v1 junto:
 *   dois juízes do mesmo turno, que é o invariante N4 quebrado por um objeto
 *   compartilhado. Esta escuta é própria e local.
 * - **Não pede o microfone de novo.** `criarEscutaDeBargeIn` (v1) chama
 *   `pedirMicrofoneComFallback` porque lá o mic está fechado enquanto a IA
 *   fala. Aqui ele está **aberto e ligado ao WebRTC** — abrir um segundo
 *   stream do mesmo dispositivo é supérfluo e disputa o hardware.
 * - **Não mata o job.** `interromper` são os dois primeiros estados da D-11
 *   (para a fala, descarta o texto); a consulta em voo segue, que é o N17.
 */
import { criarMedidorDeNivel } from './captura-de-voz'
import { criarVadDeBargeIn } from './vad-de-voz'

/**
 * @param {Object} params
 * @param {MediaStream} params.stream O microfone JÁ aberto pelo transporte.
 * @param {Function} params.iaEstaFalando Sinal de comando do leitor de eventos.
 * @param {Function} params.aoDetectar Chamado quando o consultor fala por cima.
 * @returns {{iniciar: Function, parar: Function}}
 */
export const criarEscutaDeBargeInDoTempoReal = ({ stream, iaEstaFalando, aoDetectar }) => {
  let _medidor = null

  // ⚠️ `resetar()` logo depois de disparar, e não `parar()`: a conversa
  // continua. A escuta do v1 desliga o stream ao detectar porque lá o turno
  // acaba ali; aqui a IA volta a falar no turno seguinte, e uma escuta que
  // dispara uma vez só deixaria o consultor sem interrupção do 2º turno em
  // diante — o defeito que ele acabou de relatar, de volta pela porta dos
  // fundos.
  const _vad = criarVadDeBargeIn({
    aoDetectarFala: () => {
      aoDetectar()
      _vad.resetar()
    },
  })

  const iniciar = () => {
    if (_medidor || !stream) return
    // ⚠️ O barge-in é ACESSÓRIO: ele NUNCA pode derrubar a sessão.
    // `criarMedidorDeNivel` LANÇA `GravacaoDeVozIndisponivelError` quando o
    // navegador não tem `AudioContext` — e este `iniciar` roda dentro de
    // `_concluirAbertura`, no fim de uma sessão que já foi criada e já está
    // sendo cobrada. Sem o `try`, o navegador sem WebAudio perderia a VOZ
    // INTEIRA por causa da interrupção, que é o acessório. Medido: a junção
    // do fio ao chat ([T050]) ficou vermelha nos 8 casos assim que esta
    // chamada entrou. Sem o medidor, a sessão segue — sem barge-in.
    try {
      _medidor = criarMedidorDeNivel(stream)
    } catch (erro) {
      _medidor = null
      return
    }
    _medidor.iniciar((rms) => {
      // Com a IA calada o microfone é o TURNO do consultor, não interrupção.
      // Avaliar aqui faria toda fala normal disparar o barge-in; e sem o
      // `resetar` a histerese de 300 ms atravessaria da fala dele para a
      // resposta dela, cortando o primeiro instante de cada resposta.
      if (!iaEstaFalando()) {
        _vad.resetar()
        return
      }
      _vad.avaliar(rms)
    })
  }

  const parar = () => {
    _vad.resetar()
    if (!_medidor) return
    _medidor.parar()
    _medidor = null
  }

  return { iniciar, parar }
}
